/**
 * Cliente da API do Traccar do próprio tenant.
 *
 * Mesma origem: o Caddy roteia /api para o container do cliente, então o
 * cookie de sessão funciona sem CORS e sem token no localStorage.
 */

export interface Device {
  id: number; name: string; uniqueId: string;
  status: "online" | "offline" | "unknown";
  lastUpdate: string | null; disabled: boolean;
  groupId: number; phone?: string; model?: string; contact?: string;
  category?: string;
  /**
   * Gestão de frota. Desde o ADR-0012 são COLUNAS do `tc_devices`, gravadas e
   * devolvidas pelo próprio Traccar — não mais `attributes`.
   *
   * Numérico nunca chega nulo: o model usa primitivo, e vazio vem como 0.
   */
  placa?: string; marcaVeiculo?: string; modeloVeiculo?: string; ano?: number;
  cor?: string; combustivel?: string; tanque?: number; consumo?: number;
  iccid?: string; operadora?: string; planoMb?: number; apn?: string;
  vencimentoChip?: string | null;
  serie?: string; marcaEquipamento?: string; aquisicao?: string | null;
  garantiaAte?: string | null; instalacao?: string | null; instalador?: string;
  centroCusto?: string; proprietario?: string; renavam?: string; chassi?: string;
  seguradora?: string; apoliceAte?: string | null;
  /** Nome do arquivo na pasta de mídia — a URL sai de `urlFoto`. */
  foto?: string;
  /** Só o que ainda não virou coluna — e o cadastro antigo, até ser salvo de novo. */
  attributes?: Record<string, unknown>;
}

export interface Position {
  id: number; deviceId: number; protocol: string;
  serverTime: string; deviceTime: string; fixTime: string;
  valid: boolean; latitude: number; longitude: number;
  altitude: number; speed: number; course: number;
  address: string | null; accuracy: number;
  attributes: Record<string, unknown>;
}

export interface User {
  id: number; name: string; email: string; administrator: boolean;
  /** Nome do arquivo de foto — a URL sai de `urlFoto("users", u)`. */
  foto?: string;
  /** Sessão de link compartilhado: um usuário temporário, preso a um veículo. */
  temporary?: boolean;
  readonly?: boolean;
  limitCommands?: boolean;
  disableReports?: boolean;
  /**
   * Quantos veículos e usuários esta conta pode CRIAR.
   *
   * ⚠️ Convenção do Traccar: `-1` é sem limite e `0` é nenhum. Ausente conta
   * como sem limite — é o que o servidor assume, e divergir aqui esconderia
   * aba de quem tem direito a ela.
   */
  deviceLimit?: number;
  userLimit?: number;
  expirationTime?: string | null;
  attributes?: Record<string, unknown>;
}

/** O que `/api/server` devolve. Só o que esta aplicação usa. */
export interface Servidor {
  id: number;
  /** Ligado, a preferência do SERVIDOR vence a do usuário. Ver `fusoDoTenant`. */
  forceSettings?: boolean;
  attributes: Record<string, unknown>;
}

/**
 * O fuso em que o servidor corta o dia.
 *
 * ⚠️ Isto NÃO é o fuso do navegador, e a diferença não é cosmética. Quem parte
 * a janela do resumo diário em dias é o Traccar, no fuso do tenant
 * (`UserUtil.getTimezone`). Se a tela traduzir o instante devolvido usando o
 * fuso de quem está olhando, dois dias do servidor podem cair no mesmo dia da
 * tela — duas linhas com a MESMA data para o MESMO veículo — e a tabela passa
 * a mostrar data que não existe no relatório.
 *
 * Replica `lookupStringAttribute` do 6.15.3, `forceSettings` inclusive: ligado,
 * o servidor manda; desligado, o usuário.
 */
export function fusoDoTenant(
  servidor?: Servidor | null, usuario?: User | null,
): string | undefined {
  const texto = (v: unknown) => typeof v === "string" && v ? v : undefined;
  const doServidor = texto(servidor?.attributes?.timezone);
  const doUsuario = texto(usuario?.attributes?.timezone);
  const escolhido = servidor?.forceSettings
    ? (doServidor ?? doUsuario)
    : (doUsuario ?? doServidor);
  if (escolhido) return escolhido;

  // Sem atributo nenhum, o Traccar usa o fuso da JVM — e o container sobe sem
  // TZ, ou seja, UTC (conferido no tenant atrack: `date` lá dentro responde
  // UTC). Por isso o último recurso é UTC e NÃO o fuso do navegador: assumir
  // UTC, no pior caso, desloca o rótulo; assumir o do navegador FUNDE dois
  // dias do servidor numa data só e some com o dia do meio.
  // Só quando nem o /api/server respondeu (ambos nulos) não há nada a
  // afirmar — e aí `chaveDoDia` cai no fuso do navegador, que é o único
  // palpite que resta.
  return servidor || usuario ? "UTC" : undefined;
}

/** Quantos pontos o rastro do "Seguir" guarda, e de onde sai esse número. */
export const RASTRO_PADRAO = 20;

/**
 * O agrupamento de marcadores está ligado?
 *
 * ⚠️ A ordem é **usuário, depois servidor** — ao contrário de
 * `tamanhoDoRastro`, que lê o servidor primeiro. Aqui é preferência de quem
 * olha o mapa, e é o próprio usuário que a grava ao clicar no botão; se o
 * servidor vencesse, o botão pararia de funcionar para todo mundo no instante
 * em que um administrador definisse o atributo no servidor.
 *
 * O padrão é LIGADO. Com rótulo fixo em cada pino, frota parada no mesmo pátio
 * vira um borrão de nomes sobrepostos — agrupado, o mesmo pátio é uma bolha
 * com a contagem. Quem preferir pino a pino desliga no botão, e a escolha
 * fica gravada.
 */
export function agruparMarcadores(usuario?: User | null, servidor?: Servidor | null): boolean {
  for (const fonte of [usuario?.attributes, servidor?.attributes]) {
    const v = fonte?.["mapCluster"];
    if (typeof v === "boolean") return v;
    if (v === "true" || v === "false") return v === "true";
  }
  return true;
}

/**
 * Guarda a escolha do agrupamento no usuário.
 *
 * ⚠️ Manda o usuário INTEIRO, com os atributos que já existiam. O PUT de
 * `/users/{id}` do Traccar substitui o registro: enviar só `attributes`
 * apagaria nome, e-mail e permissões.
 */
export async function salvarAgrupamento(usuario: User, ligado: boolean): Promise<User> {
  return traccar.raw<User>(`/users/${usuario.id}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      ...usuario,
      attributes: { ...(usuario.attributes ?? {}), mapCluster: ligado },
    }),
  });
}

/** Como a linha do rastro é desenhada. */
export interface EstiloRastro { cor: string | null; espessura: number; opacidade: number; }

/** Traço do rastro quando o usuário não definiu nada. */
export const ESTILO_RASTRO_PADRAO: EstiloRastro = {
  // `null` quer dizer "use a cor da marca do cliente", que só o navegador
  // sabe resolver — é variável CSS, e quem desenha é que vai lê-la.
  cor: null, espessura: 3, opacidade: 0.75,
};

/** `#rgb`, `#rrggbb` — com ou sem `#`. Qualquer outra coisa não é cor. */
const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Devolve a cor normalizada com `#`, ou `null` se não for hexadecimal.
 *
 * ⚠️ Um dono só para a regra. A cor é digitada à mão em três lugares — rastro
 * do usuário, cerca, relatório — e duas grafias da mesma validação divergem no
 * primeiro caso de borda (o `#` opcional, o atalho de três dígitos).
 */
export function corHex(valor: unknown): string | null {
  const t = String(valor ?? "").trim();
  if (!HEX.test(t)) return null;
  return t.startsWith("#") ? t : `#${t}`;
}

/**
 * Estilo do rastro ao vivo, a partir dos atributos do usuário.
 *
 * As chaves são as do traccar-web: `web.reportColor`, `mapLineWidth` e
 * `mapLineOpacity`.
 *
 * ⚠️ Cada campo cai no padrão SOZINHO. Uma cor escrita errada não pode levar
 * junto a espessura que estava certa — `attributes` é campo livre, preenchido
 * à mão, e errar um não é motivo para descartar os outros.
 *
 * ⚠️ Opacidade fora de 0–1 é recusada em vez de "corrigida". Quem escreve 50
 * provavelmente quis 50%, mas adivinhar isso silenciosamente é pior que usar o
 * padrão: o desenho sairia diferente do que a pessoa configurou, sem aviso.
 */
export function estiloDoRastro(usuario?: User | null): EstiloRastro {
  const a = usuario?.attributes ?? {};

  const cor = corHex(a["web.reportColor"]) ?? ESTILO_RASTRO_PADRAO.cor;

  const larg = Number(a["mapLineWidth"]);
  const espessura = Number.isFinite(larg) && larg > 0
    ? larg : ESTILO_RASTRO_PADRAO.espessura;

  const op = Number(a["mapLineOpacity"]);
  const opacidade = Number.isFinite(op) && op > 0 && op <= 1
    ? op : ESTILO_RASTRO_PADRAO.opacidade;

  return { cor, espessura, opacidade };
}

/**
 * Tamanho do rastro ao vivo: servidor, depois usuário, depois 20.
 *
 * ⚠️ A ordem é essa de propósito e vem do traccar-web: o atributo do SERVIDOR
 * vence o do usuário. É o administrador dizendo "nesta instalação o rastro é
 * deste tamanho" — e quem paga a conta do navegador de todo mundo é ele.
 *
 * ⚠️ Valor inválido (texto, zero, negativo) cai no padrão em vez de desenhar
 * um rastro vazio ou infinito. `attributes` é campo livre: ninguém garante que
 * o que está lá é número.
 */
export function tamanhoDoRastro(servidor?: Servidor | null, usuario?: User | null): number {
  for (const fonte of [servidor?.attributes, usuario?.attributes]) {
    const n = Number(fonte?.["web.liveRouteLength"]);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  }
  return RASTRO_PADRAO;
}

/**
 * O que a sessão pode fazer.
 *
 * O servidor já recusa o que não é permitido, mas menu que leva a 403 é
 * interface mentindo: quem abre um link compartilhado não deve ver "Cadastros".
 */
export interface Permissoes {
  relatorios: boolean;
  cadastros: boolean;
  comandos: boolean;
  /**
   * Pode gerar link de acompanhamento.
   *
   * ⚠️ Separado de `comandos`: são coisas diferentes. Comando vai para o
   * EQUIPAMENTO e depende de `limitCommands`; o link é um recurso CRIADO na
   * plataforma — um token com prazo que dá acesso sem senha. Quem é
   * somente-leitura não cria nada, então não cria isso também.
   */
  compartilhar: boolean;
  conta: boolean;
  compartilhada: boolean;

  /** A tela de Eventos aparece no menu. */
  eventos: boolean;
  /** Os botões de ação do veículo aparecem no mapa (lista e painel). */
  acoesVeiculo: boolean;
  /** O editor de atributos aparece no cadastro de usuário. */
  atributosNoUsuario: boolean;
  /** Quais abas de Cadastros aparecem. */
  abas: Record<AbaCadastro, boolean>;
}

/** As abas da tela de Cadastros, pelo id que a própria tela usa. */
export type AbaCadastro =
  | "devices" | "groups" | "drivers" | "geofences" | "maintenance"
  | "notifications" | "commands" | "computed" | "calendars" | "reports" | "users";

/**
 * Um `ui.*` do Traccar está ligado?
 *
 * ⚠️ Só esconde quando é EXPLICITAMENTE verdadeiro. Atributo ausente significa
 * "não configurado", e tratar ausência como `true` esconderia tudo de todo
 * mundo no dia em que a leitura falhasse.
 *
 * ⚠️ Aceita a string `"true"` porque o editor de atributos grava texto: quem
 * digita a chave à mão produz `"true"`, e comparar só com o booleano faria a
 * configuração não ter efeito nenhum — em silêncio, que é o pior.
 */
function uiLigado(u: User | null, chave: string): boolean {
  const v = u?.attributes?.[`ui.${chave}`];
  return v === true || v === "true";
}

/** O mesmo, para o caso em que o EXPLÍCITO `false` é que libera. */
function uiDesligadoExplicito(u: User | null, chave: string): boolean {
  const v = u?.attributes?.[`ui.${chave}`];
  return v === false || v === "false";
}

/**
 * Pode criar, pelo limite da conta?
 *
 * `0` é o único valor que proíbe: `-1` e ausente são "sem limite", e qualquer
 * positivo é uma cota, não uma proibição.
 */
const temCota = (limite: number | undefined) => limite !== 0;

export function permissoesDe(u: User | null): Permissoes {
  const t = !!u?.temporary;
  const admin = !!u?.administrator;
  return {
    relatorios: !!u && !u.disableReports && !t,
    cadastros: !!u && !u.readonly && !t,
    comandos: !!u && !u.limitCommands && !u.readonly,
    compartilhar: !!u && !u.readonly && !t,
    conta: !!u && !t,
    compartilhada: t,

    // ⚠️ Estes são PREFERÊNCIA DE INTERFACE, não permissão. Quem tem o
    // endereço continua alcançando a tela, e o servidor é quem recusa o que
    // não é permitido. Servem para não oferecer o que aquela conta não usa —
    // tirar da frente, não trancar.
    eventos: !uiLigado(u, "disableEvents"),
    acoesVeiculo: !uiLigado(u, "disableVehicleFeatures"),
    // Administrador sempre vê; para os demais, só o `false` explícito libera.
    atributosNoUsuario: admin || uiDesligadoExplicito(u, "disableAttributes"),

    abas: {
      // Sem cota de veículo não há o que cadastrar aqui.
      devices: admin || temCota(u?.deviceLimit),
      users: admin || temCota(u?.userLimit),
      // Duas razões independentes para sumir: não poder mandar comando, e a
      // conta ter desligado os roteiros salvos.
      commands: (admin || !u?.limitCommands) && !uiLigado(u, "disableSavedCommands"),
      groups: !uiLigado(u, "disableGroups"),
      drivers: !uiLigado(u, "disableDrivers"),
      maintenance: !uiLigado(u, "disableMaintenance"),
      notifications: !uiLigado(u, "disableNotifications"),
      computed: !uiLigado(u, "disableComputedAttributes"),
      calendars: !uiLigado(u, "disableCalendars"),
      // Sem regra pedida: cercas e relatórios seguem para todos.
      geofences: true,
      reports: true,
    },
  };
}

export interface Evento {
  id: number; type: string; eventTime: string; deviceId: number;
  positionId: number | null; geofenceId: number | null;
  maintenanceId: number | null; attributes: Record<string, unknown>;
}

export interface Geofence {
  id: number; name: string; description: string | null; area: string;
  /** `color` em hexadecimal é o que o mapa usa para pintar a cerca. */
  attributes?: Record<string, unknown>;
  /** 0 ou ausente = sem calendário, que é como o Traccar representa "sempre". */
  calendarId?: number;
}

/**
 * Uma parada, como o relatório do Traccar a devolve.
 *
 * ⚠️ `duration` vem em MILISSEGUNDOS, e `address` costuma vir vazio aqui pelo
 * mesmo motivo das posições: o endereço não é mais gravado (invariante 13).
 */
export interface Parada {
  deviceId: number; deviceName: string;
  latitude: number; longitude: number;
  startTime: string; endTime: string; duration: number;
  address: string | null;
  engineHours?: number; spentFuel?: number;
}

export interface Motorista {
  id: number; name: string; uniqueId: string;
  /** Colunas de `tc_drivers` desde o ADR-0012 — ver `docs/18`. */
  cpf?: string; nascimento?: string | null;
  cnh?: string; categoriaCnh?: string;
  validadeCnh?: string | null; primeiraCnh?: string | null;
  telefone?: string; admissao?: string | null; desligamento?: string | null;
  centroCusto?: string; observacoes?: string;
  /** Nome do arquivo na pasta de mídia — a URL sai de `urlFoto`. */
  foto?: string;
  attributes?: Record<string, unknown>;
}

/**
 * Comandos do Traccar (`src/resources/l10n/en.json`, chaves `command*`).
 *
 * ⚠️ `engineStop` CORTA O MOTOR. Num veículo em movimento isso é risco de
 * vida. Comando marcado como `perigoso` exige confirmação digitada.
 */
export const COMANDOS = {
  engineStop:    { rotulo: "Bloquear motor",    perigoso: true },
  engineResume:  { rotulo: "Desbloquear motor", perigoso: false },
  alarmArm:      { rotulo: "Armar alarme",      perigoso: false },
  alarmDisarm:   { rotulo: "Desarmar alarme",   perigoso: false },
  positionSingle:{ rotulo: "Pedir posição",     perigoso: false },
} as const;

export type Comando = keyof typeof COMANDOS;

export interface Manutencao {
  id: number; name: string; type: string;
  start: number; period: number; attributes: Record<string, unknown>;
}

/**
 * Roteiro de configuração: uma sequência de comandos crus, na ordem.
 *
 * ⚠️ `body` guarda as linhas como foram digitadas. Linha vazia e linha
 * começando com `#` não viram comando — ver `linhasDoRoteiro`.
 */
export interface Roteiro {
  id: number;
  name: string;
  body: string;
  description?: string;
  attributes?: Record<string, unknown>;
}

/** Atributos que o Traccar entrega dentro de `position.attributes`. */
export interface Telemetria {
  ignition?: boolean; motion?: boolean; blocked?: boolean; charge?: boolean;
  batteryLevel?: number; battery?: number; power?: number;
  sat?: number; rssi?: number; odometer?: number; totalDistance?: number;
  hours?: number; alarm?: string; event?: string; status?: string;
  temp1?: number; coolantTemp?: number; engineTemp?: number;
  fuel1?: number; fuel?: number; driverUniqueId?: string;
  in1?: boolean; in2?: boolean; out1?: boolean; out2?: boolean;
}

/** Em `npm run dev`, sem backend, a UI roda com dados de exemplo. */
const MOCK = import.meta.env.DEV && import.meta.env.VITE_MOCK !== "0";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  if (MOCK) return mock(path, init) as T;
  const r = await fetch(`/api${path}`, {
    credentials: "same-origin",
    headers: { accept: "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (r.status === 401) throw new Unauthorized();
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.status === 204 ? (undefined as T) : (r.json() as Promise<T>);
}

export class Unauthorized extends Error {
  constructor() { super("não autenticado"); this.name = "Unauthorized"; }
}

export const traccar = {
  /** Escape hatch para endpoints montados fora daqui (relatórios). */
  raw: <T>(path: string, init?: RequestInit) => req<T>(path, init),
  session: () => req<User>("/session"),
  login: (email: string, password: string) =>
    req<User>("/session", {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ email, password }),
    }),
  logout: () => req<void>("/session", { method: "DELETE" }),
  /**
   * ⚠️ Sem `todos`, traz só os equipamentos ligados ao usuário da sessão — e
   * **administrador costuma não ter nenhum ligado**, então vê uma lista vazia.
   * Foi o que aconteceu na tela de configuração em lote: ela mostrava "nenhum
   * equipamento" para quem tem acesso a todos.
   *
   * `todos` é seguro para qualquer um: o Traccar aplica o filtro de permissão
   * do mesmo jeito para quem não é administrador — conferido no
   * `DeviceResource`. O padrão segue `false` porque o mapa e o painel querem a
   * frota DO usuário, não a da empresa inteira.
   */
  devices: (todos = false) => req<Device[]>(`/devices${todos ? "?all=true" : ""}`),
  /** Preferências da instalação — usado pelo tamanho do rastro ao vivo. */
  servidor: () => req<Servidor>("/server"),
  positions: () => req<Position[]>("/positions"),
  /**
   * Preferências do servidor. Interessa o `textEnabled`: sem gateway de SMS
   * configurado ele vem `false`, e a tela precisa saber ANTES de tentar — do
   * contrário a pessoa manda para cinquenta equipamentos e colhe cinquenta
   * erros iguais.
   */
  server: () => req<{ textEnabled: boolean; [k: string]: unknown }>("/server"),

  /**
   * Roteiros de configuração de rastreador (ADR-0019).
   *
   * ⚠️ Entidade do Traccar, em `/api/commandtemplates` — **não** o
   * `/api/commands`, que é o comando salvo dele, um por registro e com tipo
   * conhecido. São conceitos diferentes.
   *
   * `all=true` traz todos (só administrador); sem ele, traz os do usuário da
   * sessão. O filtro por permissão é do próprio Traccar.
   */
  roteiros: (todos = false) =>
    req<Roteiro[]>(`/commandtemplates${todos ? "?all=true" : ""}`),
  roteiroCriar: (r: Omit<Roteiro, "id">) =>
    req<Roteiro>("/commandtemplates", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(r),
    }),
  roteiroSalvar: (r: Roteiro) =>
    req<Roteiro>(`/commandtemplates/${r.id}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(r),
    }),
  roteiroApagar: (id: number) =>
    req<void>(`/commandtemplates/${id}`, { method: "DELETE" }),

  /**
   * Vincula ou desvincula um roteiro a um usuário.
   *
   * ⚠️ É o `/api/permissions` padrão do Traccar, o mesmo que vincula
   * dispositivo e motorista. A chave `commandTemplateId` vem do
   * `Permission.getKey(CommandTemplate.class)` — camelCase aqui, minúsculo no
   * banco, e quem traduz é o Traccar.
   */
  roteiroPermissao: (userId: number, commandTemplateId: number, ligar: boolean) =>
    req<void>("/permissions", {
      method: ligar ? "POST" : "DELETE",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId, commandTemplateId }),
    }),
  /**
   * Manda UM comando cru agora.
   *
   * ⚠️ `type: "custom"` com `attributes.data` é como o Traccar transporta
   * comando que ele não conhece — e é o caso de tudo que vem de um roteiro de
   * configuração, que é dialeto do fabricante do rastreador.
   *
   * ⚠️ `textChannel: true` manda por SMS, e exige gateway no servidor **e**
   * `phone` no cadastro do equipamento. `false` manda pela conexão aberta do
   * rastreador, e exige que ele esteja online.
   */
  enviarComandoCru: async (deviceId: number, data: string, textChannel: boolean) => {
    // ⚠️ Precisa do CÓDIGO da resposta, não só do corpo: medido em 2026-10-07,
    // o Traccar devolve **202** quando o equipamento está offline e o comando
    // fica na FILA — podendo ficar dias — e 200 quando saiu agora. Tratar os
    // dois como "enviado" diria à pessoa que o rastreador foi configurado
    // quando nada chegou nele. Por isso este não passa pelo `req`.
    const r = await fetch("/api/commands/send", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ deviceId, type: "custom", textChannel, attributes: { data } }),
    });
    if (r.status === 401) throw new Unauthorized();
    if (!r.ok) {
      // O corpo do erro do Traccar é um stack trace Java inteiro. A primeira
      // linha é a mensagem; o resto é ruído na cara de quem está configurando.
      const bruto = await r.text().catch(() => "");
      const primeira = bruto.split("\n")[0].trim();
      throw new Error(`${r.status}${primeira ? ` ${primeira.slice(0, 160)}` : ""}`);
    }
    return { naFila: r.status === 202 };
  },
  /**
   * A posição de UM registro, pelo id que o evento carrega.
   *
   * ⚠️ Devolve `null` quando a posição não existe mais, e isso é rotina, não
   * exceção: as posições vivem numa janela de retenção e somem com a partição,
   * enquanto o evento continua guardado com o `positionId` apontando para o
   * vazio.
   *
   * ⚠️ E some de um jeito feio. Medido em 2026-09-30: id inexistente devolve
   * **HTTP 400** com `NullPointerException` no corpo — não 404, não lista
   * vazia. Quem trata só 404 acha que é falha de rede e mostra erro para o
   * usuário por causa de um evento de mês passado.
   */
  posicao: async (id: number): Promise<Position | null> => {
    try {
      const lista = await req<Position[]>(`/positions?id=${id}`);
      return lista?.[0] ?? null;
    } catch {
      return null;
    }
  },
  /** Histórico de um dispositivo. A janela é limitada pela retenção do plano. */
  route: (deviceId: number, from: Date, to: Date) =>
    req<Position[]>(`/positions?deviceId=${deviceId}` +
                    `&from=${from.toISOString()}&to=${to.toISOString()}`),
  /**
   * Paradas do veículo no período.
   *
   * O Traccar calcula a parada a partir das posições — não é uma tabela
   * própria. Por isso a janela aqui é a mesma do histórico, limitada pela
   * retenção do plano.
   */
  paradas: (deviceId: number, from: Date, to: Date) =>
    req<Parada[]>(`/reports/stops?deviceId=${deviceId}` +
                  `&from=${from.toISOString()}&to=${to.toISOString()}`),
  /** Eventos de UM veículo no período — o do feed cobre a frota inteira. */
  eventosDe: (deviceId: number, from: Date, to: Date) =>
    req<Evento[]>(`/reports/events?deviceId=${deviceId}&type=allEvents` +
                  `&from=${from.toISOString()}&to=${to.toISOString()}`),
  /** Eventos recentes de toda a frota — alimenta o feed de alertas. */
  eventos: (from: Date, to: Date, tipos: string[] = ["allEvents"]) =>
    req<Evento[]>(`/reports/events?from=${from.toISOString()}&to=${to.toISOString()}` +
                  tipos.map(t => `&type=${t}`).join("")),
  geofences: () => req<Geofence[]>("/geofences"),
  manutencoes: () => req<Manutencao[]>("/maintenance"),
  /** Manutenções vinculadas a UM veículo. O Traccar só consulta neste sentido:
   *  o vínculo é uma permission, e não existe endpoint que liste os veículos
   *  de uma manutenção. Daí a varredura limitada em `vinculosManutencao`. */
  manutencoesDo: (deviceId: number) =>
    req<Manutencao[]>(`/maintenance?deviceId=${deviceId}`),
  motoristas: () => req<Motorista[]>("/drivers"),
  /** Enfileira um comando. O Traccar responde 200 mesmo sem o equipamento ter recebido. */
  comandar: (deviceId: number, type: Comando) =>
    req<{ id: number }>("/commands/send", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ deviceId, type }),
    }),

  /**
   * Liga ou desliga o envio de vídeo de um canal.
   *
   * ⚠️ Só o **jt808** tem estes comandos (`Jt808ProtocolEncoder`). O vídeo em
   * si não vem por ele: chega numa conexão separada, no protocolo **jt1078**,
   * que precisa estar liberado para o cliente (Conexão › Protocolos). Sem a
   * porta do jt1078 publicada, o comando é aceito e o vídeo nunca aparece.
   */
  video: (deviceId: number, ligar: boolean, canal: number) =>
    req<{ id: number }>("/commands/send", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        deviceId,
        type: ligar ? "videoStart" : "videoStop",
        attributes: { index: canal },
      }),
    }),

  /**
   * Pede a lista de trechos gravados no cartão SD (0x9205).
   *
   * ⚠️ Canal **0 é todos**, e é o padrão: quem abre a tela quer ver o cartão
   * inteiro, não adivinhar em qual câmera o trecho ficou.
   *
   * ⚠️ Isto só MANDA o pedido. A câmera responde num 0x1205 quando quiser, e a
   * resposta é buscada depois em `/api/videolist` — ver `lib/cartaoSd.ts`.
   */
  listarCartao: (deviceId: number, canal: number, de: string, ate: string) =>
    req<{ id: number }>("/commands/send", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        deviceId,
        type: "queryVideoList",
        attributes: { index: canal, from: de, to: ate },
      }),
    }),
};

/**
 * Playlist HLS de um canal. O servidor devolve uma playlist VAZIA quando ainda
 * não chegou quadro nenhum — não é erro, é "o equipamento ainda não começou".
 */
export const urlVideo = (deviceId: number, canal: number) =>
  `/api/stream/${deviceId}/${canal}/live.m3u8`;

/** Protocolos que sabem transmitir vídeo ao vivo. Hoje, um. */
export const TEM_VIDEO = ["jt808"];

/**
 * Uma linha do log ao vivo do Traccar.
 *
 * É o quadro CRU que o rastreador mandou, antes de virar posição. Serve para
 * descobrir por que um equipamento não aparece: sem `deviceId`, o servidor
 * recebeu e não reconheceu o identificador.
 */
export interface LinhaLogAoVivo {
  time: string;
  uniqueId: string;
  protocol: string;
  data: string;
  deviceId?: number;
}

/**
 * WebSocket de posições ao vivo.
 *
 * O Traccar exige sessão autenticada antes do upgrade. Reconecta com backoff:
 * rastreamento que cai e não volta sozinho é pior que rastreamento que atrasa.
 */
function mock(path: string, init?: RequestInit): unknown {
  if (path === "/session") {
    if (init?.method === "DELETE") return undefined;
    return { id: 1, name: "Operador", email: "op@demo", administrator: false };
  }
  if (path === "/devices") return MOCK_DEVICES;
  if (path.startsWith("/positions")) return MOCK_POSITIONS;
  if (path.startsWith("/reports/events")) return MOCK_EVENTOS;
  if (path === "/geofences") return MOCK_CERCAS;
  if (path.startsWith("/maintenance?deviceId=")) {
    const id = Number(path.split("=")[1]);
    return MOCK_MANUTENCOES.filter((_, i) => (id + i) % 3 !== 0);
  }
  if (path === "/maintenance") return MOCK_MANUTENCOES;
  if (path === "/drivers") return MOCK_MOTORISTAS;
  if (path === "/commands/send") return { id: Math.floor(Math.random() * 1e6) };
  return [];
}

const NOMES = ["ABC-1D23 Scania R450", "DEF-4G56 Volvo FH", "GHI-7J89 VW Delivery",
  "JKL-0M12 Mercedes Accelo", "NOP-3Q45 Iveco Daily", "RST-6U78 Ford Cargo",
  "VWX-9Y01 Scania P310", "BCD-2E34 Volvo VM", "FGH-5I67 MAN TGX",
  "JKM-8N90 Hyundai HR", "PQR-1S23 Fiat Ducato", "TUV-4W56 Renault Master"];

const MOCK_DEVICES: Device[] = NOMES.map((name, i) => ({
  id: i + 1, name, uniqueId: `86${(i + 1).toString().padStart(13, "0")}`,
  status: i % 7 === 6 ? "offline" : "online",
  lastUpdate: new Date(Date.now() - i * 60000).toISOString(),
  disabled: false, groupId: 0,
}));

const MOCK_POSITIONS: Position[] = MOCK_DEVICES.map((d, i) => {
  const ang = (i / MOCK_DEVICES.length) * Math.PI * 2;
  return {
    id: 1000 + i, deviceId: d.id, protocol: "gt06",
    serverTime: new Date().toISOString(), deviceTime: new Date().toISOString(),
    fixTime: new Date(Date.now() - i * 60000).toISOString(),
    valid: true,
    latitude: -23.55 + Math.sin(ang) * 0.09 + (i % 3) * 0.01,
    longitude: -46.63 + Math.cos(ang) * 0.11 + (i % 4) * 0.01,
    altitude: 760, speed: d.status === "offline" ? 0 : (i % 3 === 0 ? 0 : 12 + i * 2.5),
    course: (i * 31) % 360, address: null, accuracy: 5,
    attributes: {
      motion: i % 3 !== 0, ignition: d.status === "online",
      blocked: i % 9 === 0, charge: i % 4 !== 0,
      batteryLevel: [92, 78, 64, 41, 27, 88, 55, 12, 70, 96, 33, 81][i],
      power: 12.4 + (i % 5) * 0.3, sat: 7 + (i % 6), rssi: 12 + (i % 20),
      totalDistance: 1_200_000 + i * 87_000, hours: 3_600_000 * (120 + i * 9),
      temp1: 62 + (i % 14), fuel1: [78, 45, 92, 12, 63, 30, 55, 88, 21, 70, 40, 95][i],
      ...(i === 3 ? { alarm: "hardBraking" } : {}),
      ...(i === 7 ? { alarm: "powerCut" } : {}),
      ...(i < 6 ? { driverUniqueId: `CNH${1000 + i}` } : {}),
    },
  };
});

const TIPOS_EV = ["deviceOverspeed", "alarm", "geofenceExit", "geofenceEnter",
  "deviceOffline", "deviceOnline", "deviceMoving", "deviceStopped", "ignitionOn",
  "maintenance", "deviceFuelDrop"];

const MOCK_EVENTOS: Evento[] = Array.from({ length: 28 }, (_, i) => {
  const type = TIPOS_EV[i % TIPOS_EV.length];
  return {
    id: 5000 + i, type, deviceId: (i % 12) + 1,
    eventTime: new Date(Date.now() - i * 420000).toISOString(),
    positionId: 1000 + (i % 12), geofenceId: null, maintenanceId: null,
    attributes: type === "deviceOverspeed" ? { speed: 22 + i * 1.4, speedLimit: 19.4 }
              : type === "alarm" ? { alarm: ["sos", "powerCut", "jamming", "hardBraking"][i % 4] }
              : {},
  };
});

const MOCK_CERCAS: Geofence[] = ["Garagem Central", "Zona Sul", "Rota SP-RJ",
  "Pátio Guarulhos", "Cliente Alfa", "Área restrita"].map((name, i) => ({
  id: i + 1, name, description: null, area: "",
}));

const MOCK_MOTORISTAS: Motorista[] = [
  "Carlos Aparecido Lima", "Marcos Vinícius Souza", "João Batista Ferreira",
  "Antônio Carlos Pereira", "Luiz Henrique Alves", "Paulo Sérgio Nunes",
].map((name, i) => ({ id: i + 1, name, uniqueId: `CNH${1000 + i}` }));

const MOCK_MANUTENCOES: Manutencao[] = [
  { id: 1, name: "Troca de óleo", type: "totalDistance", start: 0, period: 10000000, attributes: {} },
  { id: 2, name: "Revisão dos freios", type: "totalDistance", start: 0, period: 20000000, attributes: {} },
  { id: 3, name: "Filtro de ar", type: "hours", start: 0, period: 360000000, attributes: {} },
];

/**
 * Vínculos manutenção↔veículo, uma requisição por veículo — é o que a API
 * permite. Limitado em dois eixos para não afogar o Traccar numa frota grande:
 * no máximo `teto` veículos e `paralelas` requisições simultâneas. O retorno
 * diz quantos foram varridos, para o painel não mentir sobre a cobertura.
 */
export async function vinculosManutencao(
  deviceIds: number[], { teto = 150, paralelas = 6 } = {},
): Promise<{ vinculos: Map<number, Manutencao[]>; varridos: number }> {
  const alvos = deviceIds.slice(0, teto);
  const vinculos = new Map<number, Manutencao[]>();
  let cursor = 0;

  const worker = async () => {
    while (cursor < alvos.length) {
      const id = alvos[cursor++];
      const ms = await traccar.manutencoesDo(id).catch(() => [] as Manutencao[]);
      if (ms.length) vinculos.set(id, ms);
    }
  };
  await Promise.all(Array.from({ length: Math.min(paralelas, alvos.length) }, worker));
  return { vinculos, varridos: alvos.length };
}

export function liveSocket(handlers: {
  onPositions?: (p: Position[]) => void;
  onDevices?: (d: Device[]) => void;
  /**
   * Evento empurrado pelo servidor.
   *
   * ⚠️ Só chega aqui o que tem notificação cadastrada com o canal **web**:
   * `NotificatorWeb` é o único que chama `connectionManager.updateEvent`. O
   * filtro é do servidor — não há o que peneirar deste lado.
   */
  onEvents?: (e: Evento[]) => void;
  onStatus?: (s: "conectando" | "ao-vivo" | "reconectando") => void;
  /**
   * Linhas de log ao vivo.
   *
   * ⚠️ Chegam DESLIGADAS. O servidor só as envia depois de o cliente mandar
   * `{"logs": true}` — e são muitas: todo quadro de todo rastreador da frota.
   * Ligar sem necessidade é encher a conexão de quem só quer ver o mapa.
   */
  onLogs?: (l: LinhaLogAoVivo[]) => void;
}): { parar: () => void; logs: (ligar: boolean) => void } {
  if (MOCK) {
    handlers.onStatus?.("ao-vivo");
    return { parar: () => {}, logs: () => {} };
  }
  let ws: WebSocket | null = null;
  let closed = false;
  let querLogs = false;
  let attempt = 0;
  let timer: number | undefined;

  const connect = () => {
    if (closed) return;
    handlers.onStatus?.(attempt === 0 ? "conectando" : "reconectando");
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    ws = new WebSocket(`${proto}//${location.host}/api/socket`);

    ws.onopen = () => {
      attempt = 0;
      handlers.onStatus?.("ao-vivo");
      // ⚠️ Reenvia o pedido a CADA conexão. Depois de uma queda o servidor não
      // lembra que os logs estavam ligados — a tela ficaria muda e pareceria
      // que o rastreador parou de falar.
      if (querLogs) ws?.send(JSON.stringify({ logs: true }));
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.positions?.length) handlers.onPositions?.(msg.positions);
        if (msg.devices?.length) handlers.onDevices?.(msg.devices);
        if (msg.events?.length) handlers.onEvents?.(msg.events);
        if (msg.logs?.length) handlers.onLogs?.(msg.logs);
      } catch { /* quadro malformado não derruba a conexão */ }
    };
    ws.onclose = () => {
      if (closed) return;
      // backoff com teto de 30 s, para não martelar o servidor numa queda longa
      const wait = Math.min(1000 * 2 ** attempt++, 30000);
      handlers.onStatus?.("reconectando");
      timer = window.setTimeout(connect, wait);
    };
    ws.onerror = () => ws?.close();
  };

  connect();
  return {
    parar: () => { closed = true; clearTimeout(timer); ws?.close(); },
    logs: (ligar: boolean) => {
      querLogs = ligar;
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ logs: ligar }));
      }
    },
  };
}
